import React, { FC, Fragment, useEffect, useState } from 'react';
import {
  Box,
  FormHelperText,
  Grid,
  Typography,
  useTheme,
} from '@material-ui/core';
import useContentMgmt, {
  PageTextContentType,
  ProductStatus,
} from '../../../hooks/useContentMgmt';
import {
  subText1,
  requiredText,
  boldText,
  subTextWithoutMargin,
  dropdownTitle,
  smartIcon,
} from './quoteCss';
import { useComponentSize } from '../../../util/style-utils';
import { CardinalDirection } from '../quote.types';
import Dropdown from '../../dropdown';
import { TextAreaInput } from '../../pge-plus-common/text-area-input';
import CurrencyTextField from '../../currency-text-field';
import colors from '../../../themes/main-colors';
import ExpandIcon from '../../pge-plus-common/static/Expand.svg';
import CollapseIcon from '../../pge-plus-common/static/Collapse.svg';
import {
  Application,
  ApplicationEv,
  ApplicationEvSectionType,
  BreakerAmperageRating,
  PanelDistance,
  PanelLocation,
  SectionEvChargerSelection,
  PanelSideLocation,
} from '../../../__generated__/pgeplus-types';
import microcopyGroupIds from '../../pge-plus-common/microcopyGroupIds';
import { FormState } from '../../../hooks/useFormState.types';
import { JobQuoteModal } from '../confirm-cost/QuoteForm.rules';
import useUtil from '../../need-more-time-to-pay/useUtil';
import getJobTypeOptions from './JobTypeOptions';
import { toCurrencyString } from '../../../util/format';

interface Props {
  title: string | undefined;
  applicationData: Application | undefined;
  content: PageTextContentType;
  jobQuoteForm: FormState<JobQuoteModal>;
  calculateTotalAmount: (arg: boolean, {}: any) => void;
  resetExpandFormData: () => void;
  setShowSerialNumber?: (arg: boolean) => void;
  setIsPanelUpgrade: (arg: boolean) => void;
  panelRebateAmount: number;
  totalInstallationMaterialCost: number;
  totalInstallationCost: number;
  chargerModelList: { label: string; value: string }[];
  setChargerModelList: (
    arg: {
      value: string;
      label: string;
    }[],
  ) => void;
}

const JobInstallationCostRequote: FC<Props> = ({
  title,
  applicationData,
  content,
  jobQuoteForm,
  calculateTotalAmount,
  resetExpandFormData,
  setShowSerialNumber,
  setIsPanelUpgrade,
  panelRebateAmount,
  totalInstallationCost,
  setChargerModelList,
  chargerModelList,
}: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const { getProductList, inlineRichText } = useContentMgmt(
    microcopyGroupIds.PGE_PLUS_CONFIRM_YOUR_CHARGER,
  );
  const { setErrorNotification } = useUtil();
  const onBlurHandler = () => {
    const {
      totalInstallationMaterialCost,
      chargerCost,
      meterBase,
      weatherHead,
      electricBreaker,
      labor,
      miscMaterial,
      material,
      permitCost,
      travelCost,
      feesCost,
      miscellaneousWorkCost,
    } = jobQuoteForm.values;
    calculateTotalAmount(isOpen, {
      totalInstallationMaterial: totalInstallationMaterialCost,
      chargerCost,
      meterBase,
      weatherHead,
      electricBreaker,
      labor,
      miscMaterial,
      material,
      permitCost,
      travelCost,
      feesCost,
      miscellaneousWorkCost,
    });
  };
  useEffect(() => {
    const selectCharger = (applicationData as ApplicationEv)?.details?.find(
      sec => sec.sectionType === ApplicationEvSectionType.EvChargerSelection,
    ) as SectionEvChargerSelection;
    const fetchProductList = async () => {
      const productList = await getProductList({
        productListEntryId: selectCharger?.data?.suppliedByCustomer
          ? microcopyGroupIds.PGE_PLUS_CONFIRM_YOUR_CHARGER_PRODUCT_LIST
          : microcopyGroupIds.PGE_PLUS_SELECT_CHARGER_PRODUCT_LIST,
        status: ProductStatus.Active,
      });
      const chargerValue = productList.map(item => ({
        value: item?.id,
        label: item?.entryName,
      }));
      setChargerModelList(chargerValue || []);
    };
    if (selectCharger) {
      fetchProductList().catch(err => {
        setErrorNotification(true);
      });
    }
  }, [applicationData]);

  const distanceList = [
    {
      value: PanelDistance.Lt_5,
      label: content.get('REUSABLE_DISTANCE_5_FEET_OR_LESS'),
    },
    {
      value: PanelDistance.Btw_6_30,
      label: content.get('REUSABLE_DISTANCE_6_TO_30'),
    },
    {
      value: PanelDistance.Gt_30,
      label: content.get('REUSABLE_DISTANCE_MORE_THAN_30'),
    },
  ];

  const amperageList = [
    {
      value: BreakerAmperageRating.Amps_100,
      label: `${content.get('100_AMPS')}`,
    },
    {
      value: BreakerAmperageRating.Amps_125,
      label: `${content.get('125_AMPS')}`,
    },
    {
      value: BreakerAmperageRating.Amps_150,
      label: `${content.get('150_AMPS')}`,
    },
    {
      value: BreakerAmperageRating.Amps_200,
      label: `${content.get('200_AMPS')}`,
    },
    {
      value: BreakerAmperageRating.Amps_320,
      label: `${content.get('320_AMPS')}`,
    },
  ];

  const cardinalDirectionsList = [
    {
      value: CardinalDirection.North,
      label: `${content.get('REUSABLE_CARDINAL_DIRECTION_NORTH')}`,
    },
    {
      value: CardinalDirection.West,
      label: `${content.get('REUSABLE_CARDINAL_DIRECTION_WEST')}`,
    },
    {
      value: CardinalDirection.North_West,
      label: `${content.get('REUSABLE_CARDINAL_DIRECTION_NORTHWEST')}`,
    },
    {
      value: CardinalDirection.South_West,
      label: `${content.get('REUSABLE_CARDINAL_DIRECTION_SOUTHWEST')}`,
    },
    {
      value: CardinalDirection.South,
      label: `${content.get('REUSABLE_CARDINAL_DIRECTION_SOUTH')}`,
    },
    {
      value: CardinalDirection.East,
      label: `${content.get('REUSABLE_CARDINAL_DIRECTION_EAST')}`,
    },
    {
      value: CardinalDirection.South_East,
      label: `${content.get('REUSABLE_CARDINAL_DIRECTION_SOUTHEAST')}`,
    },
    {
      value: CardinalDirection.North_East,
      label: `${content.get('REUSABLE_CARDINAL_DIRECTION_NORTHEAST')}`,
    },
  ];

  const yesNoOptions = [
    {
      value: 'yes',
      label: `${content.get('REUSABLE_YES')}`,
    },
    {
      value: 'no',
      label: `${content.get('REUSABLE_NO')}`,
    },
  ];

  const outerPanelLocationList = [
    {
      value: PanelSideLocation.Left,
      label: `${content.get('REUSABLE_LEFT')}`,
    },
    {
      value: PanelSideLocation.BackWall,
      label: `${content.get('REUSABLE_BACK')}`,
    },
    {
      value: PanelSideLocation.Right,
      label: `${content.get('REUSABLE_RIGHT')}`,
    },
  ];

  const panelLocationList = [
    {
      value: PanelLocation.GarageAttached,
      label: `${content.get('ATTACHED_GARAGE')}`,
    },
    {
      value: PanelLocation.Basement,
      label: `${content.get('BASEMENT')}`,
    },
    {
      value: PanelLocation.Inside,
      label: `${content.get('INSIDE_THE_HOME')}`,
    },
    {
      value: PanelLocation.Outside,
      label: `${content.get('OUTSIDE_THE_HOME')}`,
    },
    {
      value: PanelLocation.NotSure,
      label: `${content.get('NOT_SURE')}`,
    },
    {
      value: PanelLocation.Other,
      label: `${content.get('OTHER')}`,
    },
  ];
  const theme = useTheme();
  const anchorEl = React.useRef<null | HTMLElement>(null);
  const { width } = useComponentSize(anchorEl);

  const hr = {
    border: '1px solid #D7D7D7',
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
  };
  const totalInstallation = {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: theme.spacing(2.5),
    fontSize: '20px',
    color: colors.noirBlur,
    lineHeight: '26px',
  };
  const individualBox = {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  };
  const onExpand = () => {
    void setIsOpen(true);
  };

  const onCollapse = async () => {
    void setIsOpen(false);
  };
  return (
    <Fragment>
      <Grid container>
        <Typography css={{ ...subText1, ...boldText, fontSize: '16px' }}>
          {title}
        </Typography>
        <Grid item xs={12} css={{ marginTop: theme.spacing(2) }}>
          <Typography css={{ ...requiredText }}>
            {content.get('REQUIRED')}{' '}
          </Typography>
          <Typography css={{ ...subTextWithoutMargin }}>
            {content.get('JOB_TYPE')}{' '}
          </Typography>
          <Dropdown
            data-testid="jobType"
            ref={anchorEl}
            selectionList={getJobTypeOptions(content)}
            {...jobQuoteForm.props('jobType')}
            onChange={e => {
              void jobQuoteForm.setValue('jobType', e.target.value);
            }}
            error={Boolean(jobQuoteForm.errors.jobType)}
          />
          {jobQuoteForm.errors.jobType && (
            <FormHelperText error={true}>
              {jobQuoteForm.errors.jobType}
            </FormHelperText>
          )}
        </Grid>
      </Grid>
      <hr css={{ ...hr }} />
      <Grid>
        <Box css={{ marginBottom: theme.spacing(2) }}>
          <Typography css={{ ...subText1 }}>
            Charger installation location
          </Typography>
        </Box>
        <Grid css={{ marginTop: theme.spacing(2) }}>
          <Typography css={{ ...dropdownTitle }}>
            {content.get('REUSABLE_MAIN_BREAKER_PANEL_LOCATION')}
          </Typography>
          <Dropdown
            data-testid="panelLocation"
            selectionList={panelLocationList}
            {...jobQuoteForm.props('panelLocation')}
            onChange={(e: any) => {
              void jobQuoteForm.setValue('panelLocation', e.target.value);
            }}
          />
        </Grid>
        {jobQuoteForm.values.panelLocation === PanelLocation.Other && (
          <Grid css={{ marginTop: theme.spacing(2) }}>
            <TextAreaInput
              {...jobQuoteForm.props('other')}
              rowsMax={8}
              rowsMin={8}
              value={jobQuoteForm.values.other as string}
              placeholder={`${content.get('ADDITIONAL_COMMENTS')}`}
              errorMsg={(jobQuoteForm.errors.other as string) || ''}
            />
          </Grid>
        )}
        <Grid css={{ marginTop: theme.spacing(2) }}>
          <Typography css={{ ...dropdownTitle }}>
            {content.get('DISTANCE_FROM_CHARGER_TO_PANEL')}
          </Typography>
          <Dropdown
            data-testid="distance"
            selectionList={distanceList}
            {...jobQuoteForm.props('distance')}
            onChange={(e: any) => {
              void jobQuoteForm.setValue('distance', e.target.value);
            }}
          />
        </Grid>
        <Grid css={{ marginTop: theme.spacing(2) }}>
          <Typography css={{ ...dropdownTitle }}>
            {content.get('WHERE_IS_PANEL_LEFT_BACK_RIGHT')}
          </Typography>
          <Dropdown
            data-testid="outsidePanelLocation"
            selectionList={outerPanelLocationList}
            {...jobQuoteForm.props('outsidePanelLocation')}
            onChange={(e: any) => {
              void jobQuoteForm.setValue(
                'outsidePanelLocation',
                e.target.value,
              );
            }}
          />
        </Grid>
      </Grid>
      <hr css={{ ...hr }} />
      <Grid>
        <Box css={{ marginTop: theme.spacing(2) }}>
          <Typography css={{ ...subText1 }}>
            {content.get('MAIN_ELECTRICAL_BREAKER_PANEL_DETAILS')}
          </Typography>
        </Box>
        <Grid css={{ marginTop: theme.spacing(2) }}>
          <Typography
            css={{
              ...boldText,
              fontSize: '14px',
              marginBottom: theme.spacing(1),
            }}
          >
            {content.get('REPLACE_PANEL_QUESTION_HEADER')}
          </Typography>
          <Box
            css={{
              ...dropdownTitle,
              marginBottom: theme.spacing(1),
            }}
          >
            {inlineRichText(content.get('REPLACE_PANEL_TWO_BULLETS'))}
          </Box>
          <Box css={{ marginTop: theme.spacing(1) }}>
            <Dropdown
              data-testid="isReplace"
              selectionList={yesNoOptions}
              {...jobQuoteForm.props('isReplace')}
              onChange={(e: any) => {
                void jobQuoteForm.setValue('isReplace', e.target.value);
                setIsPanelUpgrade(e.target.value === 'yes' ? true : false);
                if (e.target.value === 'no') {
                  void jobQuoteForm.setValue('amperage', '');
                }
              }}
            />
          </Box>
        </Grid>
        {jobQuoteForm.values.isReplace === 'yes' && (
          <Grid css={{ marginTop: theme.spacing(2) }}>
            <Typography css={{ ...dropdownTitle }}>
              {content.get('CONFIRM_AMPERAGE_MAIN_BREAKER')}
            </Typography>
            <Dropdown
              data-testid="amperage"
              selectionList={amperageList}
              {...jobQuoteForm.props('amperage')}
              onChange={(e: any) => {
                void jobQuoteForm.setValue('amperage', e.target.value);
              }}
            />
          </Grid>
        )}
      </Grid>
      <hr css={{ ...hr }} />
      <Grid>
        <Box css={{ marginTop: theme.spacing(2) }}>
          <Typography css={{ ...subText1 }}>
            {content.get('CHARGER_EQUIPMENT')}
          </Typography>
        </Box>
        <Grid css={{ marginTop: theme.spacing(2) }}>
          <Typography css={{ ...dropdownTitle }}>
            {content.get('EV_CHARGER_MODEL')}
          </Typography>
          <Dropdown
            data-testid="chargerModel"
            selectionList={chargerModelList}
            {...jobQuoteForm.props('chargerModel')}
            onChange={(e: any) => {
              const { innerText } = e.nativeEvent.target;
              void jobQuoteForm.setValue('chargerModel', e.target.value);
              if (!innerText.toLowerCase().includes('juicebox')) {
                setShowSerialNumber && setShowSerialNumber(true);
              } else {
                setShowSerialNumber && setShowSerialNumber(false);
              }
            }}
          />
          <Box css={{ marginTop: theme.spacing(2) }}>
            <Typography css={{ ...subText1 }}>
              {content.get('COSTS_CHARGER')}
            </Typography>
            <CurrencyTextField
              css={{
                margin: 0,
                width: '100%',
                '& label': {
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  width: '90%',
                  '&.MuiInputLabel-shrink': {
                    overflow: 'unset',
                  },
                },
              }}
              label={content.get('EV_CHARGER_COSTS') || ''}
              name={'chargerCost'}
              {...jobQuoteForm.props('chargerCost')}
              onBlur={onBlurHandler}
            />
          </Box>
        </Grid>
      </Grid>
      <hr css={{ ...hr }} />
      <Grid>
        <Box css={{ marginTop: theme.spacing(2) }}>
          <Typography css={{ ...subText1 }}>
            {content.get('COSTS_INSTALLATION')}
          </Typography>
        </Box>
        <Box css={{ marginTop: theme.spacing(2) }}>
          <Typography
            css={{
              ...boldText,
              color: colors.noirBlurLight,
              marginBottom: theme.spacing(2),
              fontSize: '14px',
              lineHeight: '18px',
            }}
          >
            {content.get('INSTALLER_COSTS_INSTRUCTIONS')}
          </Typography>
          <Typography css={{ ...requiredText }}>
            {content.get('REQUIRED')}
          </Typography>
          <CurrencyTextField
            css={{
              width: '100%',
              '& label': {
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                width: '90%',
                '&.MuiInputLabel-shrink': {
                  overflow: 'unset',
                },
              },
            }}
            disabled={isOpen}
            label={`${content.get('INSTALLATION_AND_MATERIALS')}` || ''}
            name={'totalInstallationMaterialCost'}
            {...jobQuoteForm.props('totalInstallationMaterialCost')}
            onBlur={() => {
              onBlurHandler();
              resetExpandFormData();
            }}
          />
        </Box>
        <Box>
          <Box css={{ ...individualBox }}>
            {isOpen ? (
              <CollapseIcon
                css={{ ...smartIcon }}
                onClick={() => onCollapse()}
              />
            ) : (
              <ExpandIcon css={{ ...smartIcon }} onClick={() => onExpand()} />
            )}
            <Typography
              css={{
                fontStyle: 'italic',
                fontSize: '20px',
                lineHeight: '26px',
                margin: '20px 0',
              }}
            >
              {content.get('REUSABLE_ADD_ADDITIONAL_LINE_ITEMS')}
            </Typography>
          </Box>
          {isOpen && (
            <Box css={{ marginLeft: theme.spacing(3) }}>
              <CurrencyTextField
                css={{
                  margin: 0,
                  width: '100%',
                  '& label': {
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    width: '90%',
                    '&.MuiInputLabel-shrink': {
                      overflow: 'unset',
                    },
                  },
                  marginTop: theme.spacing(3),
                }}
                label={`${content.get('REUSABLE_METER_BASE')}` || ''}
                name={'meterBase1'}
                {...jobQuoteForm.props('meterBase')}
                onBlur={onBlurHandler}
              />

              <CurrencyTextField
                css={{
                  margin: 0,
                  width: '100%',
                  '& label': {
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    width: '90%',
                    '&.MuiInputLabel-shrink': {
                      overflow: 'unset',
                    },
                  },
                  marginTop: theme.spacing(3),
                }}
                label={`${content.get('REUSABLE_WEATHERHEAD')}` || ''}
                name={'weatherHead'}
                {...jobQuoteForm.props('weatherHead')}
                onBlur={onBlurHandler}
              />
              <CurrencyTextField
                css={{
                  margin: 0,
                  width: '100%',
                  '& label': {
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    width: '90%',
                    '&.MuiInputLabel-shrink': {
                      overflow: 'unset',
                    },
                  },
                  marginTop: theme.spacing(3),
                }}
                label={`${content.get('REUSABLE_NEW_PANEL')}` || ''}
                name={'electricalBreaker'}
                {...jobQuoteForm.props('electricBreaker')}
                onBlur={onBlurHandler}
              />
              <CurrencyTextField
                css={{
                  margin: 0,
                  width: '100%',
                  '& label': {
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    width: '90%',
                    '&.MuiInputLabel-shrink': {
                      overflow: 'unset',
                    },
                  },
                  marginTop: theme.spacing(3),
                }}
                label={`${content.get('REUSABLE_LABOR')}` || ''}
                name={'labor'}
                {...jobQuoteForm.props('labor')}
                onBlur={onBlurHandler}
              />
              <CurrencyTextField
                css={{
                  margin: 0,
                  width: '100%',
                  '& label': {
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    width: '90%',
                    '&.MuiInputLabel-shrink': {
                      overflow: 'unset',
                    },
                  },
                  marginTop: theme.spacing(3),
                }}
                label={`${content.get('REUSABLE_MISC_MATERIALS')}` || ''}
                name={'miscMaterials'}
                {...jobQuoteForm.props('miscMaterial')}
                onBlur={onBlurHandler}
              />
              <CurrencyTextField
                css={{
                  margin: 0,
                  width: '100%',
                  '& label': {
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    width: '90%',
                    '&.MuiInputLabel-shrink': {
                      overflow: 'unset',
                    },
                  },
                  marginTop: theme.spacing(3),
                }}
                label={`${content.get('REUSABLE_MATERIALS')}`}
                name={'materials'}
                {...jobQuoteForm.props('material')}
                onBlur={onBlurHandler}
              />
            </Box>
          )}
          <Typography css={{ ...requiredText }}>
            {content.get('REQUIRED')}
          </Typography>
          <CurrencyTextField
            css={{
              margin: 0,
              width: '100%',
              '& label': {
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                width: '90%',
                '&.MuiInputLabel-shrink': {
                  overflow: 'unset',
                },
              },
              marginTop: theme.spacing(1),
            }}
            label={`${content.get('REUSABLE_PERMITS')}` || ''}
            name={'permits'}
            {...jobQuoteForm.props('permitCost')}
            onBlur={onBlurHandler}
            error={Boolean(jobQuoteForm.errors.permitCost)}
            helperText={jobQuoteForm.errors.permitCost}
          />
          <Typography css={{ ...requiredText, marginTop: theme.spacing(3) }}>
            {content.get('OPTIONAL_TEXT')}
          </Typography>
          <CurrencyTextField
            css={{
              margin: 0,
              width: '100%',
              '& label': {
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                width: '90%',
                '&.MuiInputLabel-shrink': {
                  overflow: 'unset',
                },
              },
              marginTop: theme.spacing(1.2),
            }}
            label={`${content.get('REUSABLE_TRAVEL_TIME')}` || ''}
            name={'travelCost'}
            {...jobQuoteForm.props('travelCost')}
            onBlur={onBlurHandler}
            error={Boolean(jobQuoteForm.errors.travelCost)}
            helperText={jobQuoteForm.errors.travelCost}
          />
          <Typography css={{ ...requiredText, marginTop: theme.spacing(3) }}>
            {content.get('OPTIONAL_TEXT')}
          </Typography>
          <CurrencyTextField
            css={{
              margin: 0,
              width: '100%',
              '& label': {
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                width: '90%',
                '&.MuiInputLabel-shrink': {
                  overflow: 'unset',
                },
              },
              marginTop: theme.spacing(1.2),
            }}
            label={`${content.get('REUSABLE_FEES')}` || ''}
            name={'fees'}
            {...jobQuoteForm.props('feesCost')}
            onBlur={onBlurHandler}
            error={Boolean(jobQuoteForm.errors.feesCost)}
            helperText={jobQuoteForm.errors.feesCost}
          />
          <Typography css={{ ...requiredText, marginTop: theme.spacing(3) }}>
            {content.get('OPTIONAL_TEXT')}
          </Typography>
          <CurrencyTextField
            css={{
              margin: 0,
              width: '100%',
              '& label': {
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                width: '90%',
                '&.MuiInputLabel-shrink': {
                  overflow: 'unset',
                },
              },
              marginTop: theme.spacing(1.2),
            }}
            label={`${content.get('REUSABLE_MISC_WORK')}` || ''}
            name={'miscellaneousWork'}
            {...jobQuoteForm.props('miscellaneousWorkCost')}
            onBlur={onBlurHandler}
            error={Boolean(jobQuoteForm.errors.miscellaneousWorkCost)}
            helperText={jobQuoteForm.errors.miscellaneousWorkCost}
          />
          <Box css={{ ...totalInstallation }}>
            <Typography>{content.get('TOTAL_INSTALLATION_COSTS')}</Typography>
            <Typography>
              ${toCurrencyString(Number(totalInstallationCost), true)}
            </Typography>
          </Box>
        </Box>
      </Grid>
      <hr css={{ ...hr }} />
      <Grid>
        <TextAreaInput
          {...jobQuoteForm.props('additionalComment')}
          rowsMax={8}
          rowsMin={8}
          value={jobQuoteForm.values.additionalComment}
          placeholder={`${content.get('ADDITIONAL_COMMENTS')}`}
          errorMsg={jobQuoteForm.errors.additionalComment as string}
        />
      </Grid>
      <hr css={{ ...hr }} />
      <Grid>
        <Box css={{ marginTop: theme.spacing(2) }}>
          <Typography css={{ ...subText1 }}>
            {content.get('REBATE_SECTION')}
          </Typography>
        </Box>
        <Box css={{ marginTop: theme.spacing(2) }}>
          <Typography css={{ ...boldText, fontWeight: 400 }}>
            {content.get('REBATE_ELIGIBILITY')}:
          </Typography>
          <Typography css={{ ...boldText }}>
            {jobQuoteForm.values.rebateEligibility}
          </Typography>
        </Box>
        <Box css={{ marginTop: theme.spacing(2) }}>
          <Typography css={{ ...boldText, fontWeight: 400 }}>
            {content.get('TYPE_OF_REBATE')}
          </Typography>
          <Typography css={{ ...boldText }}>
            {jobQuoteForm.values.rebateType || 'N/A'}
          </Typography>
        </Box>
        <Box css={{ marginTop: theme.spacing(2) }}>
          <Typography css={{ ...boldText, fontWeight: 400 }}>
            {content.get('PANEL_UPGRADE_COLON')}
          </Typography>
          <Typography css={{ ...boldText }}>
            {panelRebateAmount > 0
              ? content.get('REUSABLE_YES')
              : content.get('REUSABLE_NO')}
          </Typography>
        </Box>
      </Grid>
    </Fragment>
  );
};

export default JobInstallationCostRequote;
